import { createApiSlice } from 'app/createApiSlice'
import { API_MUTATION_REPORT } from 'utils/apiList'
import {
  TRACE_CACHE_TTL,
  hashString,
  readTraceCache,
  traceCacheKey,
  writeTraceCache,
} from './traceCache'

// Trace responses stay in memory this long after the page stops using them,
// so switching direction (or coming back) shows them without a reload
const TRACE_KEEP_SECONDS = 10 * 60

// Query that is served from the persistent trace cache when possible:
// BACKWARD responses are kept 14 days, FORWARD responses 1 day.
const cachedTraceQuery =
  (url, toParams) => async (args, api, extraOptions, baseQuery) => {
    const params = toParams(args)
    const state = api.getState()
    const userId = state.dashboard?.ownerData?.userId
    const userKey = userId
      ? `u${userId}`
      : state.auth?.token
        ? `t${hashString(state.auth.token)}`
        : null

    // `refreshAt`: the user asked for data newer than this moment, so older
    // entries are skipped and the API rebuilds its baked report too
    const apiParams = args.refreshAt ? { ...params, refresh: true } : params

    // Without a known user, skip the cache so accounts never mix
    if (!userKey)
      return baseQuery({ url, params: apiParams }, api, extraOptions)

    // `maxCacheAge` lets a caller ask for fresher data than the default
    // lifetime, e.g. current stock read from the BACKWARD trace
    const ttl =
      args.maxCacheAge ??
      (args.backward ? TRACE_CACHE_TTL.backward : TRACE_CACHE_TTL.forward)
    const maxAge = args.refreshAt
      ? Math.min(ttl, Date.now() - args.refreshAt)
      : ttl
    const key = traceCacheKey(userKey, url, params)
    const cached = await readTraceCache(key, maxAge)
    if (cached !== null) return { data: cached }

    const result = await baseQuery(
      { url, params: apiParams },
      api,
      extraOptions,
    )
    if (!result.error) {
      writeTraceCache(key, result.data, ttl, args.primaryLotId)
    }
    return result
  }

export const mutationReportApiSlice = createApiSlice.injectEndpoints({
  reducerPath: 'mutationReport/api',
  endpoints: (builder) => ({
    fetchMutationSummary: builder.query({
      keepUnusedDataFor: TRACE_KEEP_SECONDS,
      queryFn: cachedTraceQuery(
        '/api/v1/bussines/laporevents/summary',
        ({
          escrowpaymentId,
          filterSummary = 'ALL',
          primaryLotId,
          backward = undefined,
        }) => ({
          escrowpaymentId,
          filterSummary,
          primaryLotId,
          backward,
        }),
      ),
    }),

    fetchMutationInventories: builder.query({
      keepUnusedDataFor: TRACE_KEEP_SECONDS,
      queryFn: cachedTraceQuery(
        '/api/v1/bussines/laporevents/inventories',
        ({
          escrowpaymentId,
          page = 0,
          size = 10,
          primaryLotId,
          backward = undefined,
          search = undefined,
          sortby = undefined,
          sortdir = undefined,
        }) => ({
          escrowpaymentId,
          page,
          size,
          primaryLotId,
          backward,
          search: search || undefined,
          sortby,
          sortdir,
        }),
      ),
    }),

    fetchMutationReport: builder.query({
      query(args) {
        const { primaryLotId, size, page, sortdir, sortby, search } = args
        const params = {
          primaryLotId,
          size,
          page,
          sortdir: sortdir?.toUpperCase(),
          sortby,
          search,
        }

        return {
          url: API_MUTATION_REPORT,
          params,
        }
      },
    }),
  }),
})

export const {
  useFetchMutationSummaryQuery,
  useLazyFetchMutationSummaryQuery,
  useFetchMutationInventoriesQuery,
  useLazyFetchMutationInventoriesQuery,
  useFetchMutationReportQuery,
  useLazyFetchMutationReportQuery,
} = mutationReportApiSlice
